<template>
  <header
    class="app-header"
    :class="{
      'is-sticky': isSticky,
      'user-logged-in': isLoggedIn,
      'is-home': isHome,
      'is-subpage': !isHome
    }"
  >
    <div class="header-container">
      <!-- Main Only Green Menu Bar: Seamless Solid Green -->
      <div class="header-pill-bar">
        <!-- Mobile Menu Toggle Button -->
        <button @click="toggleSidebar" class="mobile-toggle-btn" aria-label="Open menu">
          <i class="fas fa-bars"></i>
        </button>

        <!-- Brand Logo (Far Left) -->
        <div class="header-logo-box">
          <a :href="route('home')" class="logo-link">
            <img
              v-if="hasLogoImg"
              :src="logoUrl"
              :alt="props.appName"
              class="logo-img"
              @error="onLogoError"
            />
            <span v-else class="logo-text-fallback">{{ (props.appName || 'STORE').toUpperCase() }}</span>
          </a>
        </div>

        <!-- Dynamic Category Navigation (Tight Spacing, No Huge Gaps) -->
        <nav v-if="mainCategories.length > 0" class="category-nav desktop-only" aria-label="Main categories">
          <div
            v-for="cat in mainCategories"
            :key="cat.slug"
            class="nav-category-item"
            @mouseenter="showSubMenu(cat.slug)"
            @mouseleave="hideSubMenu"
          >
            <a :href="cat.link" class="nav-category-link">
              <span class="nav-category-title">{{ cat.name.toUpperCase() }}</span>
              <i v-if="cat.subcategories && cat.subcategories.length > 0" class="fas fa-chevron-down nav-sub-chevron"></i>
            </a>

            <!-- Subcategories Dropdown (Max 5 Subcategories, Pure White Background) -->
            <div
              v-if="cat.subcategories && cat.subcategories.length > 0"
              class="category-dropdown"
              :class="{ 'is-active': activeSubCategory === cat.slug }"
            >
              <div class="category-dropdown-inner">
                <a
                  v-for="sub in cat.subcategories"
                  :key="sub.slug"
                  :href="sub.link"
                  class="dropdown-sub-link"
                >
                  {{ sub.name }}
                </a>
              </div>
            </div>
          </div>
        </nav>

        <!-- Right Action Items: BAG [0], Search & Account (Sitting Close to Each Other) -->
        <div class="header-right-group">
          <!-- Shopping Bag Link (Matching Green with Yellow Badge) -->
          <a :href="route('cart.view')" class="bag-link" aria-label="Shopping Bag">
            <i class="fas fa-shopping-bag bag-icon-white"></i>
            <span class="bag-badge">{{ localCartCount }}</span>
          </a>

          <!-- Search Icon Box (Matching Green) -->
          <button @click="toggleSearch" class="header-action-box search-toggle-btn" aria-label="Search">
            <i class="fas fa-search action-icon-white"></i>
          </button>

          <!-- User / Login Icon Box (Matching Green) -->
          <div class="user-box-container">
            <template v-if="isLoggedIn">
              <button @click="toggleUserMenu" class="header-action-box" aria-label="Account" title="Account">
                <img v-if="userAvatar" :src="userAvatar" :alt="userName" class="user-avatar-tiny" />
                <i v-else class="far fa-user action-icon-white"></i>
              </button>
              <div v-show="showUserMenu" class="user-dropdown-card">
                <div class="user-card-header">
                  <strong>{{ userName }}</strong>
                </div>
                <hr />
                <a :href="route('dashboard')" class="user-card-item">Dashboard</a>
                <a :href="route('dashboard', 'orders')" class="user-card-item">My Orders</a>
                <a :href="route('dashboard', 'wishlist')" class="user-card-item">Saved Items</a>
                <hr />
                <form @submit.prevent="handleLogout">
                  <button type="submit" class="user-card-item logout-btn">Sign Out</button>
                </form>
              </div>
            </template>
            <template v-else>
              <a :href="route('login')" class="header-action-box" aria-label="Sign In" title="Sign In">
                <i class="far fa-user action-icon-white"></i>
              </a>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Slide-Down Search Bar (Desktop & Mobile fallback) -->
    <div v-show="isSearchOpen" class="search-slide-bar">
      <form @submit.prevent="handleSearch" class="search-slide-form">
        <input
          ref="searchInputRef"
          v-model="searchQuery"
          type="search"
          placeholder="Search products, collections..."
          class="search-slide-input"
        />
        <button type="submit" class="search-slide-submit" aria-label="Search">
          <i class="fas fa-search"></i>
        </button>
        <button type="button" @click="isSearchOpen = false" class="search-slide-close" aria-label="Close search">
          <i class="fas fa-times"></i>
        </button>
      </form>
    </div>

  </header>
</template>

<script setup>
import { ref, computed, nextTick, onMounted } from 'vue'
import { useAuthStore } from '../../store/auth'

const props = defineProps({
  user: {
    type: Object,
    default: null
  },
  cartCount: {
    type: Number,
    default: 0
  },
  appName: {
    type: String,
    default: 'regiis'
  },
  appUrl: {
    type: String,
    default: '/'
  },
  appLocale: {
    type: String,
    default: 'en'
  },
  categories: {
    type: Array,
    default: () => []
  }
})

const authStore = useAuthStore()

const showUserMenu = ref(false)
const activeSubCategory = ref(null)
const searchQuery = ref('')
const isSearchOpen = ref(false)
const searchInputRef = ref(null)
const isSticky = ref(false)
const isHome = ref(true)
const localCartCount = ref(props.cartCount)
const hasLogoImg = ref(true)

const logoUrl = ref('/logos/logo.svg')

const onLogoError = () => {
  if (logoUrl.value === '/logos/logo.svg') {
    logoUrl.value = '/logos/logo.png'
  } else {
    hasLogoImg.value = false
  }
}

const isLoggedIn = computed(() => !!props.user)
const userName = computed(() => {
  const user = props.user
  return user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email : ''
})
const userAvatar = computed(() => props.user?.image || null)

// Parse categories and limit subcategories to max 5
const allParsedCategories = computed(() => {
  return (props.categories || []).map(cat => {
    const rawSubs = cat.subCategories || cat.sub_categories || cat.children || cat.subcategories || cat.subs || []
    return {
      name: cat.name,
      slug: cat.slug,
      link: `/${cat.slug}`,
      subcategories: (Array.isArray(rawSubs) ? rawSubs : []).slice(0, 5).map(sub => ({
        name: sub.name,
        slug: sub.slug,
        link: `/${cat.slug}/${sub.slug}`
      }))
    }
  })
})

// Keep exactly 4 categories on the menu
const mainCategories = computed(() => {
  return allParsedCategories.value.slice(0, 4)
})

const route = (name, param = '') => {
  const routes = {
    'home': '/',
    'products.search': '/search',
    'trending': '/trending',
    'today-deals': '/today-deals',
    'cart.view': '/cart',
    'dashboard': param ? `/dashboard/${param}` : '/dashboard',
    'orders': '/orders',
    'login': '/login',
    'register': '/register'
  }
  return routes[name] || '/'
}

const toggleUserMenu = () => {
  showUserMenu.value = !showUserMenu.value
}

const toggleSidebar = () => {
  window.dispatchEvent(new CustomEvent('toggle-sidebar'))
}

const toggleSearch = () => {
  isSearchOpen.value = !isSearchOpen.value
  if (isSearchOpen.value) {
    nextTick(() => {
      searchInputRef.value?.focus()
    })
  }
}

const showSubMenu = (slug) => {
  activeSubCategory.value = slug
}

const hideSubMenu = () => {
  activeSubCategory.value = null
}

const handleSearch = () => {
  if (searchQuery.value.trim()) {
    const baseUrl = route('products.search')
    window.location.href = `${baseUrl}?q=${encodeURIComponent(searchQuery.value)}`
  }
}

const handleLogout = () => {
  const form = document.createElement('form')
  form.method = 'POST'
  form.action = '/logout'
  const token = document.querySelector('meta[name="csrf-token"]')?.content
  const input = document.createElement('input')
  input.type = 'hidden'
  input.name = '_token'
  input.value = token
  form.appendChild(input)
  document.body.appendChild(form)
  form.submit()
}

onMounted(() => {
  if (typeof window !== 'undefined') {
    const p = window.location.pathname
    isHome.value = p === '/' || p === ''
  }

  if (props.user) {
    authStore.setUser(props.user)
  }

  if (props.cartCount !== undefined) {
    authStore.setCartCount(props.cartCount)
  }

  window.addEventListener('cart-updated', (e) => {
    if (e.detail && e.detail.count !== undefined) {
      localCartCount.value = Number(e.detail.count)
    }
  })

  window.addEventListener('scroll', () => {
    isSticky.value = window.scrollY > 20
  })

  document.addEventListener('click', (e) => {
    if (showUserMenu.value && !e.target.closest('.user-box-container')) {
      showUserMenu.value = false
    }
  })
})
</script>

<style scoped>
/* ==========================================================================
   ONLY GREEN MAIN MENU (BOTTOM SPACING ON PC, TIGHT SPACING & CLOSE RIGHT ACTIONS)
   ========================================================================== */
.app-header {
  position: relative !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  z-index: 1000 !important;
  padding: 14px 0 !important;
  transition: all 0.3s ease;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  overflow: visible !important;
  pointer-events: none;
}

/* PC Version: Moderate bottom space for menu on subpages */
@media (min-width: 869px) {
  .app-header.is-subpage {
    position: relative !important;
    padding: 16px 0 8px 0 !important;
    margin-bottom: 1.25rem !important;
  }
}

.app-header.is-sticky {
  position: fixed !important;
  top: 0 !important;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  padding: 10px 0 !important;
  margin-bottom: 0 !important;
}

.header-container {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  justify-content: center !important;
  width: 100%;
  padding: 0 1.75rem !important;
  box-sizing: border-box;
  overflow: visible !important;
  pointer-events: auto;
}

/* --- The Seamless Green Pill Bar --- */
.header-pill-bar {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  background: #0d2d24 !important; /* 100% Solid Green */
  border-radius: 10px !important;
  padding: 12px 22px !important;
  min-height: 68px !important;
  gap: 1rem !important;
  width: 100% !important;
  overflow: visible !important;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.35) !important;
  box-sizing: border-box !important;
  border: none !important;
  pointer-events: auto;
}

.header-logo-box {
  display: inline-flex !important;
  align-items: center !important;
  flex-shrink: 0 !important;
  margin-right: 0.5rem !important;
}

.logo-link {
  text-decoration: none;
  display: inline-flex;
  align-items: center;
}

.logo-img {
  height: 40px;
  max-height: 44px;
  max-width: 200px;
  width: auto;
  object-fit: contain;
  display: block;
}

.logo-text-fallback {
  font-size: 1.6rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  color: #ffffff !important;
  line-height: 1;
  white-space: nowrap;
}

/* Horizontal Category Navigation inside Green Bar (Tight Spacing) */
.category-nav {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  gap: 0.35rem !important; /* Reduced space between categories */
  flex-wrap: nowrap !important;
  margin: 0 !important;
  padding: 0 !important;
  list-style: none !important;
  overflow: visible !important;
}

.nav-category-item {
  position: relative !important;
  display: inline-flex !important;
  align-items: center !important;
  flex-shrink: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
}

/* Category Menu Links (Compact & Seamless) */
.nav-category-link {
  display: inline-flex !important;
  flex-direction: row !important;
  align-items: center !important;
  gap: 0.35rem !important;
  color: #ffffff !important;
  background: #0d2d24 !important;
  text-decoration: none !important;
  font-size: 0.88rem !important;
  font-weight: 800 !important;
  letter-spacing: 0.04em !important;
  padding: 9px 12px !important;
  border-radius: 6px !important;
  white-space: nowrap !important;
  transition: opacity 0.2s ease, background 0.2s ease, transform 0.2s ease;
  line-height: 1.2 !important;
  cursor: pointer;
  border: none !important;
}

.nav-category-link:hover {
  background: #143d32 !important;
  transform: translateY(-1px);
}

.nav-category-title {
  color: #ffffff !important;
  font-weight: 800 !important;
}

.nav-sub-chevron {
  font-size: 0.6rem !important;
  color: #ffffff !important;
  opacity: 0.9;
}

/* Subcategories Dropdown (Pure White Background) */
.category-dropdown {
  display: none;
  position: absolute !important;
  top: 100% !important;
  left: 0 !important;
  padding-top: 10px !important;
  min-width: 240px !important;
  z-index: 99999 !important;
}

.nav-category-item:hover .category-dropdown,
.category-dropdown.is-active {
  display: block !important;
}

.category-dropdown-inner {
  background: #ffffff !important;
  border: 1px solid #e0e0e0 !important;
  box-shadow: 0 16px 44px rgba(0, 0, 0, 0.22) !important;
  border-radius: 8px !important;
  padding: 0.75rem 0 !important;
}

.dropdown-sub-link {
  display: block !important;
  padding: 0.85rem 1.5rem !important;
  color: #1a1a1a !important;
  font-size: 0.9rem !important;
  font-weight: 700 !important;
  text-decoration: none !important;
  transition: background 0.15s ease, color 0.15s ease;
  white-space: nowrap !important;
  background: #ffffff !important;
}

.dropdown-sub-link:hover {
  background: #f5f5f5 !important;
  color: #000000 !important;
}

/* --- Right Action Items Group (Bag, Search, Account Sitting Close Together) --- */
.header-right-group {
  display: inline-flex !important;
  align-items: center !important;
  gap: 0.4rem !important; /* Close to each other */
  margin-left: auto !important;
  flex-shrink: 0 !important;
}

/* Bag Icon Link (icon only, count bubble on corner) */
.bag-link {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  position: relative !important;
  width: 42px !important;
  height: 42px !important;
  background: #0d2d24 !important;
  color: #ffffff !important;
  text-decoration: none !important;
  padding: 0 !important;
  border-radius: 6px !important;
  transition: opacity 0.2s ease, transform 0.2s ease;
  line-height: 1 !important;
  border: none !important;
}

.bag-link:hover {
  background: #143d32 !important;
  transform: translateY(-1px);
}

.bag-icon-white {
  color: #ffffff !important;
  font-size: 1.25rem !important;
}

.bag-badge {
  position: absolute !important;
  top: -4px !important;
  right: -4px !important;
  background: #facc15 !important;
  color: #000000 !important;
  font-size: 0.6rem !important;
  font-weight: 900 !important;
  padding: 1px 4px !important;
  border-radius: 9999px !important;
  min-width: 15px !important;
  text-align: center !important;
  line-height: 1.3 !important;
}

/* Action Icon Boxes (Search and User) */
.header-action-box {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 42px !important;
  height: 42px !important;
  border-radius: 6px !important;
  background: #0d2d24 !important;
  color: #ffffff !important;
  font-size: 1.1rem !important;
  border: none !important;
  cursor: pointer !important;
  text-decoration: none !important;
  transition: opacity 0.2s ease, transform 0.2s ease;
  line-height: 1 !important;
  padding: 0 !important;
  margin: 0 !important;
}

.header-action-box:hover {
  background: #143d32 !important;
  transform: translateY(-1px);
}

.action-icon-white {
  color: #ffffff !important;
}

.user-box-container {
  position: relative !important;
  display: inline-flex !important;
  align-items: center !important;
  overflow: visible !important;
}

.user-avatar-tiny {
  width: 28px !important;
  height: 28px !important;
  border-radius: 50% !important;
  object-fit: cover !important;
  border: 1px solid #ffffff !important;
}

.user-dropdown-card {
  position: absolute !important;
  top: calc(100% + 10px) !important;
  right: 0 !important;
  min-width: 200px !important;
  background: #ffffff !important;
  border: 1px solid #e0e0e0 !important;
  box-shadow: 0 14px 40px rgba(0, 0, 0, 0.16) !important;
  border-radius: 8px !important;
  padding: 0.6rem 0 !important;
  z-index: 99999 !important;
}

.user-card-header {
  padding: 0.6rem 1.25rem !important;
  font-size: 0.9rem !important;
  color: #111111 !important;
  font-weight: 700 !important;
}

.user-card-item {
  display: block !important;
  width: 100% !important;
  text-align: left !important;
  padding: 0.6rem 1.25rem !important;
  color: #333333 !important;
  font-size: 0.86rem !important;
  font-weight: 600 !important;
  text-decoration: none !important;
  background: #ffffff !important;
  border: none !important;
  cursor: pointer !important;
  transition: background 0.15s ease;
}

.user-card-item:hover {
  background: #f5f5f5 !important;
  color: #000000 !important;
}

.user-dropdown-card hr {
  border: none !important;
  border-top: 1px solid #eeeeee !important;
  margin: 0.4rem 0 !important;
}

/* Mobile Toggle */
.mobile-toggle-btn {
  display: none !important;
  background: transparent !important;
  border: none !important;
  color: #ffffff !important;
  font-size: 1.35rem !important;
  cursor: pointer !important;
  padding: 0 !important;
  margin: 0 !important;
  align-items: center !important;
  justify-content: center !important;
  height: 44px !important;
  width: 44px !important;
  line-height: 1 !important;
  flex-shrink: 0 !important;
}

.mobile-toggle-btn i {
  color: #ffffff !important;
}

/* Slide-Down Search Bar */
.search-slide-bar {
  width: 100% !important;
  padding: 0.85rem 2rem !important;
  background: #ffffff !important;
  border-top: 1px solid #e0e0e0 !important;
  border-bottom: 1px solid #e0e0e0 !important;
  box-sizing: border-box !important;
  pointer-events: auto;
}

.search-slide-form {
  position: relative !important;
  max-width: 750px !important;
  margin: 0 auto !important;
  display: flex !important;
  align-items: center !important;
}

.search-slide-input {
  width: 100% !important;
  height: 46px !important;
  padding: 0 75px 0 18px !important;
  background: #f5f5f5 !important;
  border: 1px solid #d5d5d5 !important;
  border-radius: 9999px !important;
  font-size: 0.95rem !important;
  color: #111111 !important;
  outline: none !important;
}

.search-slide-input:focus {
  border-color: #111111 !important;
}

.search-slide-submit {
  position: absolute !important;
  right: 40px !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  background: transparent !important;
  border: none !important;
  color: #555555 !important;
  font-size: 1rem !important;
  padding: 0 !important;
  cursor: pointer !important;
}

.search-slide-close {
  position: absolute !important;
  right: 14px !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  background: transparent !important;
  border: none !important;
  color: #777777 !important;
  font-size: 1.1rem !important;
  padding: 0 !important;
  cursor: pointer !important;
}

.desktop-only {
  display: flex !important;
}

/* ==========================================================================
   MOBILE RESPONSIVE (CLOSE RIGHT ACTIONS & COMPACT SPACING)
   ========================================================================== */
@media (max-width: 868px) {
  .app-header {
    /* Flush top bar on mobile — slider starts directly below, no gaps */
    padding: 0 !important;
    margin: 0 !important;
    position: relative !important;
  }
  .app-header.is-sticky {
    padding: 0 !important;
  }
  /* No search on mobile */
  .search-toggle-btn {
    display: none !important;
  }
  .header-container {
    padding: 0 !important;
  }
  /* Mobile pill-bar: full-width flush bar — toggle | logo (centered) | actions */
  .header-pill-bar {
    padding: 8px 10px !important;
    min-height: 54px !important;
    gap: 0 !important;
    border-radius: 0 !important;
    border: none !important;
    box-shadow: none !important;
    overflow: hidden !important;
    justify-content: space-between !important;
  }
  .desktop-only {
    display: none !important;
  }
  /* Toggle button — fixed width on the left */
  .mobile-toggle-btn {
    display: inline-flex !important;
    font-size: 1.3rem !important;
    height: 38px !important;
    width: 38px !important;
    flex-shrink: 0 !important;
    margin-right: 6px !important;
  }
  /* Logo box takes all remaining middle space, centered */
  .header-logo-box {
    flex: 1 1 0 !important;
    min-width: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    margin: 0 !important;
    overflow: hidden !important;
  }
  .logo-link {
    justify-content: center !important;
    max-width: 100% !important;
    overflow: hidden !important;
  }
  .logo-img {
    height: 30px !important;
    max-height: 34px !important;
    max-width: 120px !important;
    width: auto !important;
    object-fit: contain !important;
    flex-shrink: 1 !important;
  }
  .logo-text-fallback {
    font-size: 1.15rem !important;
    font-weight: 900 !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    max-width: 120px !important;
  }
  /* Right actions group — fixed width on the right */
  .header-right-group {
    flex-shrink: 0 !important;
    gap: 0.2rem !important;
    margin-left: 6px !important;
  }
  .header-action-box {
    width: 34px !important;
    height: 34px !important;
    font-size: 0.9rem !important;
    border: none !important;
  }
  .bag-link {
    width: 34px !important;
    height: 34px !important;
    padding: 0 !important;
    border: none !important;
  }
  .bag-icon-white {
    font-size: 1.05rem !important;
  }
  .bag-badge {
    top: -4px !important;
    right: -4px !important;
    font-size: 0.55rem !important;
    padding: 0 3px !important;
    min-width: 13px !important;
    line-height: 1.4 !important;
  }
}
</style>
